import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

// Research data from citations
const researchPapers = [
  {
    id: 'synthetic-mental-health',
    title: 'High-accuracy prediction of mental health scores from BERT embeddings trained on LLM-generated synthetic self-reports',
    authors: 'B Moëll, FS Aronsson',
    venue: 'Frontiers in Digital Health',
    year: 2026,
    category: 'Digital Health',
    abstract: 'Investigating whether artificial narratives created by LLMs can support accurate prediction of mental health assessment scores (PHQ-9, LSAS, PCL-5), offering a privacy-preserving path for method development.',
    url: 'https://www.frontiersin.org/journals/digital-health/articles/10.3389/fdgth.2025.1694464/full',
    tags: ['Mental Health', 'Synthetic Data', 'BERT', 'LLM', 'Privacy'],
    impact: 'high'
  },
  {
    id: 'deepseek-r1',
    title: 'Medical Reasoning in LLMs: An In-Depth Analysis of DeepSeek R1',
    authors: 'B Moëll, FS Aronsson, S Akbar',
    venue: 'Frontiers in Artificial Intelligence',
    year: 2025,
    category: 'LLM Evaluation',
    abstract: 'This paper provides an in-depth analysis of DeepSeek R1\'s medical reasoning capabilities, examining Chain-of-Thought reasoning patterns and their effectiveness in clinical decision-making scenarios.',
    url: 'https://www.frontiersin.org/journals/artificial-intelligence/articles/10.3389/frai.2025.1616145',
    tags: ['LLM', 'Medical AI', 'Reasoning', 'DeepSeek'],
    impact: 'high'
  },
  {
    id: 'smlb',
    title: 'Swedish Medical LLM Benchmark (SMLB)',
    authors: 'B Moëll, F Farestam, J Beskow',
    venue: 'Frontiers in Artificial Intelligence',
    year: 2025,
    category: 'Benchmarking',
    abstract: 'Development and evaluation of a comprehensive framework for assessing Large Language Models in the Swedish medical domain, establishing the first standardized benchmark for Swedish healthcare AI.',
    url: 'https://www.frontiersin.org/journals/artificial-intelligence/articles/10.3389/frai.2025.1557920',
    tags: ['Benchmark', 'Swedish', 'Medical AI', 'Evaluation'],
    impact: 'high'
  },
  {
    id: 'harm-reduction',
    title: 'Harm Reduction Strategies for LLMs in Medical Domain',
    authors: 'B Moëll, FS Aronsson',
    venue: 'Journal of Medical Internet Research',
    year: 2025,
    category: 'AI Safety',
    abstract: 'Perspectives for patients and clinicians on thoughtful use of large language models in healthcare, proposing strategies for harm reduction and responsible AI deployment.',
    url: 'https://pubmed.ncbi.nlm.nih.gov/40712151/',
    tags: ['AI Safety', 'Healthcare', 'Harm Reduction', 'Guidelines'],
    impact: 'high'
  },
  {
    id: 'artificial-humans',
    title: 'Artificial Humans',
    authors: 'B Moëll',
    venue: 'arXiv preprint',
    year: 2025,
    category: 'AI Philosophy',
    abstract: 'An exploration of artificial human systems, examining the convergence of AI capabilities towards human-like cognition and the implications for society.',
    url: 'https://arxiv.org/abs/2503.16502',
    tags: ['AI Philosophy', 'AGI', 'Cognition'],
    impact: 'medium'
  },
  {
    id: 'voice-cloning',
    title: 'Voice Cloning for Dysarthric Speech Synthesis',
    authors: 'B Moëll, FS Aronsson',
    venue: 'arXiv preprint',
    year: 2025,
    category: 'Speech Technology',
    abstract: 'Addressing data scarcity in speech-language pathology through synthetic voice generation for patients with ALS and cerebral palsy.',
    url: 'https://arxiv.org/abs/2503.01266',
    tags: ['Speech Synthesis', 'Dysarthria', 'Voice Cloning', 'ALS'],
    impact: 'medium'
  },
  {
    id: 'math-reasoning',
    title: 'Large Language Models and Mathematical Reasoning Failures',
    authors: 'J Boye, B Moëll',
    venue: 'arXiv preprint',
    year: 2025,
    category: 'LLM Evaluation',
    abstract: 'Investigation into the systematic failures of large language models in mathematical reasoning tasks, identifying patterns and limitations.',
    url: 'https://arxiv.org/abs/2502.11574',
    tags: ['LLM', 'Mathematics', 'Reasoning', 'Failures'],
    impact: 'medium'
  },
  {
    id: 'dementia-detection',
    title: 'Multimodal Capture for Early Dementia Detection',
    authors: 'P Jonell, B Moëll, K Håkansson, et al.',
    venue: 'Frontiers in Computer Science',
    year: 2021,
    category: 'Clinical AI',
    abstract: 'Clinical feasibility study on multimodal capture of patient behavior for improved detection of early dementia, combining speech, gesture, and cognitive markers.',
    url: 'https://www.frontiersin.org/articles/10.3389/fcomp.2021.642633',
    tags: ['Dementia', 'Multimodal', 'Clinical', 'Detection'],
    impact: 'high'
  },
  {
    id: 'cvpr-synthetic',
    title: 'Fake it to Make it: Synthetic Data for Speech-Gesture Synthesis',
    authors: 'S Mehta, A Deichler, J O\'regan, B Moëll, et al.',
    venue: 'CVPR Workshop (HuMoGen)',
    year: 2024,
    category: 'Multimodal AI',
    abstract: 'Using synthetic data to remedy data shortage in joint multimodal speech-and-gesture synthesis, published at the CVPR Human Motion Generation workshop.',
    url: 'https://openaccess.thecvf.com/content/CVPR2024W/HuMoGen/html/Mehta_Fake_It_to_Make_It_Using_Synthetic_Data_to_Remedy_CVPRW_2024_paper.html',
    tags: ['CVPR', 'Synthetic Data', 'Gesture', 'Speech'],
    impact: 'high'
  },
  {
    id: 'living-smart',
    title: 'Living SMART: Online CBT for Adults with ADHD',
    authors: 'B Moëll, L Kollberg, B Nasri, N Lindefors, V Kaldo',
    venue: 'Internet Interventions',
    year: 2015,
    category: 'Digital Health',
    abstract: 'World\'s first randomized controlled trial of a guided online course teaching adults with ADHD to use smartphones to structure their everyday life.',
    url: 'https://www.sciencedirect.com/science/article/pii/S2214782914000244',
    tags: ['ADHD', 'CBT', 'RCT', 'Digital Therapy'],
    impact: 'high'
  }
];

const categories = Array.from(new Set(researchPapers.map(p => p.category)));
const allTags = Array.from(new Set(researchPapers.flatMap(p => p.tags)));

const colors = {
  deep: '#0D0A0F',
  accent1: '#FCCB06',
  accent2: '#E5A83B',
  highlight: '#FFEBA3',
  text: '#FFFCF0',
  textMuted: '#F5EBD0',
};

export default function ResearchExplorer() {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [hoveredPaper, setHoveredPaper] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(true);
  }, []);

  const filteredPapers = researchPapers.filter(paper => {
    if (selectedCategory && paper.category !== selectedCategory) return false;
    if (selectedTag && !paper.tags.includes(selectedTag)) return false;
    return true;
  });

  const papersByYear = filteredPapers.reduce((acc, paper) => {
    if (!acc[paper.year]) acc[paper.year] = [];
    acc[paper.year].push(paper);
    return acc;
  }, {} as Record<number, typeof researchPapers>);

  const years = Object.keys(papersByYear).map(Number).sort((a, b) => b - a);

  return (
    <div
      className="min-h-screen text-white"
      style={{
        background: colors.deep,
        opacity: loaded ? 1 : 0,
        transition: 'opacity 1s ease-in-out'
      }}
    >
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-1/4 left-1/4 w-[60vw] h-[60vw] rounded-full opacity-20"
          style={{
            background: `radial-gradient(circle, ${colors.accent1}40, transparent 70%)`,
            filter: 'blur(100px)',
            animation: 'pulse 20s ease-in-out infinite'
          }}
        />
        <div
          className="absolute bottom-1/4 right-1/4 w-[40vw] h-[40vw] rounded-full opacity-15"
          style={{
            background: `radial-gradient(circle, ${colors.accent2}40, transparent 70%)`,
            filter: 'blur(80px)',
            animation: 'pulse 25s ease-in-out infinite reverse'
          }}
        />
        {/* Floating particles */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              background: colors.accent1,
              opacity: 0.3,
              animation: `float ${5 + Math.random() * 10}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`
            }}
          />
        ))}
      </div>

      {/* Header */}
      <header className="relative z-10 px-8 py-12">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link
            to="/"
            className="text-white/60 hover:text-amber-200 transition-colors text-sm tracking-widest"
          >
            &larr; BACK TO HOME
          </Link>
          <h1 className="text-2xl font-thin tracking-wider">
            RESEARCH <span style={{ color: colors.highlight }}>EXPLORER</span>
          </h1>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 px-8 py-20 text-center">
        <h2 className="text-5xl md:text-7xl font-thin mb-8">
          Explore the <span style={{ color: colors.highlight }}>Research</span>
        </h2>
        <p className="text-xl text-white/60 font-extralight max-w-2xl mx-auto">
          Interactive visualization of publications spanning AI, healthcare, speech technology, and cognitive science.
        </p>

        {/* Stats */}
        <div className="flex justify-center gap-16 mt-16">
          <div className="text-center">
            <div className="text-4xl font-thin" style={{ color: colors.highlight }}>{researchPapers.length}</div>
            <div className="text-white/40 text-sm tracking-wider mt-2">PUBLICATIONS</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-thin" style={{ color: colors.highlight }}>{categories.length}</div>
            <div className="text-white/40 text-sm tracking-wider mt-2">RESEARCH AREAS</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-thin" style={{ color: colors.highlight }}>2015-2026</div>
            <div className="text-white/40 text-sm tracking-wider mt-2">TIMELINE</div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="relative z-10 px-8 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Category Filter */}
          <div className="mb-8">
            <h3 className="text-sm text-white/40 tracking-wider mb-4">FILTER BY CATEGORY</h3>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-4 py-2 rounded-full text-sm transition-all ${
                  selectedCategory === null
                    ? 'bg-amber-200/20 text-amber-200 border border-amber-200/50'
                    : 'bg-white/5 text-white/60 border border-white/10 hover:border-white/30'
                }`}
              >
                All
              </button>
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat === selectedCategory ? null : cat)}
                  className={`px-4 py-2 rounded-full text-sm transition-all ${
                    selectedCategory === cat
                      ? 'bg-amber-200/20 text-amber-200 border border-amber-200/50'
                      : 'bg-white/5 text-white/60 border border-white/10 hover:border-white/30'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Tag Filter */}
          <div>
            <h3 className="text-sm text-white/40 tracking-wider mb-4">FILTER BY TAG</h3>
            <div className="flex flex-wrap gap-2">
              {allTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => setSelectedTag(tag === selectedTag ? null : tag)}
                  className={`px-3 py-1 rounded-full text-xs transition-all ${
                    selectedTag === tag
                      ? 'bg-amber-200/20 text-amber-200 border border-amber-200/50'
                      : 'bg-white/5 text-white/40 border border-white/5 hover:border-white/20'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="relative z-10 px-8 py-20">
        <div className="max-w-7xl mx-auto">
          {years.map((year, yearIndex) => (
            <div key={year} className="mb-20">
              {/* Year Marker */}
              <div className="flex items-center mb-12">
                <div
                  className="text-6xl font-thin"
                  style={{ color: colors.accent1, opacity: 0.8 }}
                >
                  {year}
                </div>
                <div
                  className="flex-grow h-px ml-8"
                  style={{ background: `linear-gradient(to right, ${colors.accent1}50, transparent)` }}
                />
              </div>

              {/* Papers Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 ml-8">
                {papersByYear[year].map((paper, index) => (
                  <a
                    key={paper.id}
                    href={paper.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative p-8 rounded-2xl transition-all duration-500 transform hover:-translate-y-2"
                    style={{
                      background: hoveredPaper === paper.id
                        ? `linear-gradient(135deg, ${colors.deep}, ${colors.accent1}15)`
                        : 'rgba(13, 10, 15, 0.6)',
                      border: `1px solid ${hoveredPaper === paper.id ? colors.accent1 : 'rgba(255,255,255,0.1)'}`,
                      backdropFilter: 'blur(10px)',
                      animationDelay: `${index * 100}ms`
                    }}
                    onMouseEnter={() => setHoveredPaper(paper.id)}
                    onMouseLeave={() => setHoveredPaper(null)}
                  >
                    {/* Impact indicator */}
                    {paper.impact === 'high' && (
                      <div
                        className="absolute top-4 right-4 w-3 h-3 rounded-full"
                        style={{
                          background: colors.highlight,
                          boxShadow: `0 0 10px ${colors.highlight}`
                        }}
                      />
                    )}

                    {/* Category Badge */}
                    <div
                      className="inline-block px-3 py-1 rounded-full text-xs mb-4"
                      style={{
                        background: `${colors.accent1}20`,
                        color: colors.accent1
                      }}
                    >
                      {paper.category}
                    </div>

                    {/* Title */}
                    <h4 className="text-xl font-thin mb-4 text-white group-hover:text-amber-200 transition-colors leading-tight">
                      {paper.title}
                    </h4>

                    {/* Authors */}
                    <p className="text-white/40 text-sm mb-4 font-extralight">
                      {paper.authors}
                    </p>

                    {/* Venue */}
                    <p className="text-amber-200/60 text-sm mb-4 font-extralight">
                      {paper.venue}
                    </p>

                    {/* Abstract */}
                    <p className="text-white/50 text-sm leading-relaxed mb-6 font-extralight line-clamp-3">
                      {paper.abstract}
                    </p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2">
                      {paper.tags.slice(0, 3).map(tag => (
                        <span
                          key={tag}
                          className="px-2 py-1 rounded text-xs"
                          style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.4)' }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Hover arrow */}
                    <div
                      className="absolute bottom-8 right-8 opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ color: colors.accent1 }}
                    >
                      &rarr;
                    </div>
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Research Themes Visualization */}
      <section className="relative z-10 px-8 py-20">
        <div className="max-w-5xl mx-auto text-center">
          <h3 className="text-3xl font-thin mb-16">
            Research <span style={{ color: colors.highlight }}>Themes</span>
          </h3>

          <div className="grid md:grid-cols-3 gap-8">
            <div
              className="p-8 rounded-2xl text-center"
              style={{
                background: 'rgba(13, 10, 15, 0.6)',
                border: `1px solid ${colors.accent1}30`
              }}
            >
              <div className="text-5xl mb-4">🧠</div>
              <h4 className="text-xl font-thin mb-2" style={{ color: colors.highlight }}>AI in Healthcare</h4>
              <p className="text-white/50 text-sm">Developing AI systems that improve diagnosis, treatment, and patient outcomes</p>
            </div>

            <div
              className="p-8 rounded-2xl text-center"
              style={{
                background: 'rgba(13, 10, 15, 0.6)',
                border: `1px solid ${colors.accent1}30`
              }}
            >
              <div className="text-5xl mb-4">🗣️</div>
              <h4 className="text-xl font-thin mb-2" style={{ color: colors.highlight }}>Speech & Language</h4>
              <p className="text-white/50 text-sm">Using speech patterns as biomarkers for neurological conditions</p>
            </div>

            <div
              className="p-8 rounded-2xl text-center"
              style={{
                background: 'rgba(13, 10, 15, 0.6)',
                border: `1px solid ${colors.accent1}30`
              }}
            >
              <div className="text-5xl mb-4">⚖️</div>
              <h4 className="text-xl font-thin mb-2" style={{ color: colors.highlight }}>AI Safety</h4>
              <p className="text-white/50 text-sm">Ensuring responsible deployment of AI in sensitive domains</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 py-20 text-center">
        <Link
          to="/"
          className="inline-block px-8 py-3 rounded-full text-sm tracking-wider transition-all hover:scale-105"
          style={{
            background: `linear-gradient(to right, ${colors.accent1}40, ${colors.accent2}30)`,
            border: `1px solid ${colors.accent1}50`
          }}
        >
          BACK TO MAIN SITE
        </Link>
        <p className="text-white/20 text-sm mt-8">
          &copy; {new Date().getFullYear()} Birger Moëll
        </p>
      </footer>

      {/* Animation Styles */}
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.2; }
          50% { transform: scale(1.1); opacity: 0.3; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}
